// ============================================================================
// BACKTEST ENGINE
// Takes a signal (ticker, direction, target%, stop%, timeframe) and replays
// it against real historical price data to compute actual win rate and
// calibrated confidence score.
//
// Returns null if no historical data available — no simulation.
// ============================================================================

import { getHistory } from './marketEngine';

// Trading-cost model — deducted from every trial's gross return so simulated
// performance reflects what the trader actually nets after frictions. Tuned for
// liquid US equities at modern retail brokerages. Raise these for illiquid
// names or higher-cost venues.
const SLIPPAGE_PCT_PER_SIDE = 0.05;        // 5 bps — half bid/ask spread on each fill
const COMMISSION_PCT_ROUND_TRIP = 0.05;    // 5 bps — typical retail round-trip cost
const ROUND_TRIP_COST_PCT =
  SLIPPAGE_PCT_PER_SIDE * 2 + COMMISSION_PCT_ROUND_TRIP;  // 15 bps total

// Run a backtest for a single signal
export async function backtestSignal(signal) {
  const { ticker, direction, targetPct, stopPct, currentPrice } = signal;
  if (!ticker || !targetPct || !stopPct || !currentPrice) return null;

  // Fetch 90 days of real history
  const history = await getHistory(ticker, 120);
  if (!history || history.length < 20) return null; // Not enough data

  const holdingDays = signal.timeframe?.includes('Intraday') ? 1 : 5;
  const absTarget = Math.abs(targetPct);
  const absStop = Math.abs(stopPct);

  const trials = [];
  // Each trial: the signal fires on bar i, entry happens at the OPEN of bar
  // i+1. The signal-bar close is information known *when the signal fires*,
  // so entering at that price is look-ahead — using next-bar-open removes it
  // and matches realistic live execution. Loop bound reserves one bar for the
  // entry plus `holdingDays` bars to evaluate the trade.
  const maxTrials = Math.min(30, history.length - holdingDays - 1);

  for (let i = 0; i < maxTrials; i++) {
    const entryBarIdx = i + 1;
    const entryBar = history[entryBarIdx];
    const entryPrice = entryBar?.open || entryBar?.close;
    if (!entryPrice || entryPrice <= 0) continue;

    let outcome = 'held'; // 'win', 'loss', or 'held' (didn't hit target or stop)
    let exitPrice = entryPrice;
    let exitDay = 0;
    let maxFavorable = 0;
    let maxAdverse = 0;

    for (let d = 0; d < holdingDays && (entryBarIdx + d) < history.length; d++) {
      const dayData = history[entryBarIdx + d];
      const high = dayData.high || dayData.close;
      const low = dayData.low || dayData.close;

      let favorableMove, adverseMove;
      if (direction === 'LONG') {
        favorableMove = ((high - entryPrice) / entryPrice) * 100;
        adverseMove = ((entryPrice - low) / entryPrice) * 100;
      } else {
        favorableMove = ((entryPrice - low) / entryPrice) * 100;
        adverseMove = ((high - entryPrice) / entryPrice) * 100;
      }

      maxFavorable = Math.max(maxFavorable, favorableMove);
      maxAdverse = Math.max(maxAdverse, adverseMove);

      // Check if target hit
      if (favorableMove >= absTarget) {
        outcome = 'win';
        exitPrice = direction === 'LONG'
          ? entryPrice * (1 + absTarget / 100)
          : entryPrice * (1 - absTarget / 100);
        exitDay = d + 1;
        break;
      }

      // Check if stop hit
      if (adverseMove >= absStop) {
        outcome = 'loss';
        exitPrice = direction === 'LONG'
          ? entryPrice * (1 - absStop / 100)
          : entryPrice * (1 + absStop / 100);
        exitDay = d + 1;
        break;
      }

      exitPrice = dayData.close;
      exitDay = d + 1;
    }

    // Gross P&L from price movement.
    const grossReturnPct = direction === 'LONG'
      ? ((exitPrice - entryPrice) / entryPrice) * 100
      : ((entryPrice - exitPrice) / entryPrice) * 100;

    // Net of round-trip slippage + commission. A trade that hits its target
    // displays the target price, but the realized P&L is reduced by frictions.
    const returnPct = grossReturnPct - ROUND_TRIP_COST_PCT;

    if (outcome === 'held') {
      outcome = returnPct > 0 ? 'win' : 'loss';
    }

    trials.push({
      entryDate: entryBar.date,
      entryPrice: +entryPrice.toFixed(2),
      exitPrice: +exitPrice.toFixed(2),
      exitDay,
      outcome,
      returnPct: +returnPct.toFixed(2),
      maxFavorable: +maxFavorable.toFixed(2),
      maxAdverse: +maxAdverse.toFixed(2),
    });
  }

  if (trials.length < 5) return null; // Not enough trials

  // Compute statistics
  const wins = trials.filter(t => t.outcome === 'win');
  const losses = trials.filter(t => t.outcome === 'loss');
  const winRate = wins.length / trials.length;
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.returnPct, 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + t.returnPct, 0) / losses.length) : 0;
  const profitFactor = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? 99 : 0;
  const totalReturn = trials.reduce((s, t) => s + t.returnPct, 0);
  const avgReturn = totalReturn / trials.length;
  const expectancy = (winRate * avgWin) - ((1 - winRate) * avgLoss);

  // Sharpe ratio (annualized)
  const returns = trials.map(t => t.returnPct);
  const mean = avgReturn;
  const stdDev = Math.sqrt(returns.reduce((s, r) => s + (r - mean) ** 2, 0) / returns.length);
  const sharpe = stdDev > 0 ? (mean / stdDev) * Math.sqrt(252 / (holdingDays || 1)) : 0;

  // Calibrated confidence — a comprehensive verdict that folds win rate,
  // avg return, profit factor, risk-adjusted return (Sharpe), sample size,
  // and data quality into a single 10-95 score. Sharpe is the dominant
  // risk-aware signal: high WR with negative Sharpe (variance-driven luck)
  // gets penalised; modest WR with strong Sharpe (consistent edge) gets
  // rewarded.
  const winRateScore = Math.min(1, winRate / 0.6) * 30;                             // 60% WR = full 30
  const returnScore  = Math.min(1, Math.max(0, avgReturn / 3)) * 15;                // 3% avg = full 15
  const rrScore      = Math.min(1, profitFactor / 2) * 10;                          // 2:1 PF = full 10
  const sharpeScore  = Math.min(1, Math.max(0, sharpe / 2)) * 20;                   // Sharpe 2.0 = full 20, negative = 0
  const sampleScore  = Math.min(1, trials.length / 20) * 10;                        // 20 trials = full 10
  const dataScore    = 15;                                                          // always full — we only use real data

  const calibratedConfidence = Math.round(Math.min(95, Math.max(10,
    winRateScore + returnScore + rrScore + sharpeScore + sampleScore + dataScore
  )));

  return {
    ticker,
    trials: trials.length,
    wins: wins.length,
    losses: losses.length,
    winRate: +(winRate * 100).toFixed(1),
    avgWin: +avgWin.toFixed(2),
    avgLoss: +avgLoss.toFixed(2),
    profitFactor: +profitFactor.toFixed(2),
    avgReturn: +avgReturn.toFixed(2),
    totalReturn: +totalReturn.toFixed(2),
    expectancy: +expectancy.toFixed(2),
    sharpe: +sharpe.toFixed(2),
    calibratedConfidence,
    holdingDays,
    trialDetails: trials,
  };
}
